import { useState, useMemo, useEffect } from 'react';
import { useDebounce } from './useDebounce';
import { normalizeArabic } from '../lib/arabicUtils';
import { allSearchableItems } from '../components/header/constants';
import { Briefcase } from 'lucide-react';

/**
 * Hook to manage search query logic and filtered suggestions.
 * Now includes dynamic services from Firestore.
 */
export function useSearchSuggestions() {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [dynamicServices, setDynamicServices] = useState<any[]>([]);

  // Fetch dynamic services from Firestore lazily to optimize initial load
  useEffect(() => {
    if (searchQuery.trim() === '') return;
    if (dynamicServices.length > 0) return; // Prevent multiple fetches once loaded

    let active = true;
    const fetchServices = async () => {
      try {
        const { db } = await import('../lib/firebase');
        const { getDocs, collection, query } = await import('firebase/firestore');
        const q = query(collection(db, 'services'));
        const snapshot = await getDocs(q);
        if (!active) return;
        const services = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Filter out non-published
        setDynamicServices(services.filter((s: any) => s.status !== 'draft' && s.status !== 'hidden'));
      } catch (err) {
        console.error('Error fetching services for search:', err);
      }
    };
    
    fetchServices();
    return () => {
      active = false;
    };
  }, [searchQuery, dynamicServices.length]);

  const filteredSuggestions = useMemo(() => {
    if (debouncedSearchQuery.trim() === '') return [];

    const normalizedQuery = normalizeArabic(debouncedSearchQuery);

    // Dynamic services mapped to SubItem format
    const dynamicItems = dynamicServices.map(s => ({
      name: s.title,
      href: s.link || `/services`, // Should ideally map to detail page
      icon: Briefcase,
      type: 'خدمة'
    }));

    // Combine hardcoded and dynamic items
    const combinedItems = [
      ...dynamicItems,
      ...allSearchableItems
    ];

    // Remove duplicates by name
    const uniqueItems = Array.from(new Map(combinedItems.map(item => [item.name, item])).values());

    return uniqueItems
      .filter(item =>
        normalizeArabic(item.name).includes(normalizedQuery) ||
        (item.type && normalizeArabic(item.type).includes(normalizedQuery))
      )
      .slice(0, 8);
  }, [debouncedSearchQuery, dynamicServices]);

  return {
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    showSuggestions,
    setShowSuggestions,
    filteredSuggestions
  };
}
